// DirectWalletAdjustModal — agent SPA variant of the admin modal.
//
// Same identity-card pattern as admin-frontend's modal, but bound by
// three agent-side business rules:
//
//   1. CREDIT ONLY. Agents cannot withdraw from a player or sub-agent
//      directly — withdrawals must come through the request queue.
//      So the direction toggle is gone; the modal is implicitly a
//      "send money to" action.
//
//   2. OWN POOL ONLY. The funding source is always the calling
//      agent's wallet. No Platform / Agent-pool toggle.
//
//   3. INSUFFICIENT_FUNDS surfaces inline. The server already throws
//      400 if the move would overdraw the agent's pool — we just
//      render that error verbatim under the form.
//
// Mounts wrap the modal at /agent SPA. Endpoints used:
//   • player subject  → POST /admin/agents/{myAgentId}/players/{userId}/recharge
//   • subagent subject → POST /admin/me-agent/sub-agents/{id}/wallet/topup
//
// Both write the appropriate wallet_ledger rows AND audit_log rows
// (audit-log writes wired in shared/.../meAgentSubAgents.controller.js
// + adminAgents.controller.js on 2026-05-16). The agent's own pool
// balance auto-refreshes via refreshAgentWallet() after a success so
// the chrome chip + "Your pool" header chip stay current.

import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { createPortal } from 'react-dom';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { refreshAgentWallet } from '@baaligames/shared-ui/admin/lib/agentWallet';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import ProofImagePicker from '@baaligames/shared-ui/components/ProofImagePicker';

// ─── Subject types ────────────────────────────────────────────────

type PlayerSubject = {
  kind: 'player';
  id: number;
  username: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  walletBalance: number;
};

type SubagentSubject = {
  kind: 'subagent';
  id: number;
  username: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  level?: number | null;
  uaid?: string | null;
  poolBalance: number;
};

export type AgentDirectAdjustSubject = PlayerSubject | SubagentSubject;

// Phase 17 — for sub-agent subjects only: which POOL the recharge
// lands on. 'wallet' is the existing /wallet/topup flow; 'deposit' is
// the new /deposit/topup flow that funds the sub-agent's security
// deposit from the parent's OPERATING wallet (strict ₹0 floor).
// Withdrawal is intentionally absent — sub-agents must submit a
// withdrawal request through the queue.
type SubagentTarget = 'wallet' | 'deposit';

export default function AgentDirectWalletAdjustModal({
  subject, onClose, onDone,
}: {
  subject: AgentDirectAdjustSubject;
  onClose: () => void;
  onDone: () => void;
}) {
  const { admin } = useAdminAuth();
  const t = useT();
  // Agent's own pool balance comes off the auth context (the chrome
  // chip subscribes via refreshAgentWallet → setAgentWalletBalance).
  // We just read from a custom hook? simpler: re-fetch on mount via
  // /admin/agents/:id when the modal opens. Keeping it inline so the
  // modal is self-sufficient and doesn't depend on the parent passing
  // the balance in (the chrome chip already shows it anyway).
  const myAgentId = admin?.id ?? null;
  const myUsername = admin?.username || 'you';
  const [myPool, setMyPool] = useState<number | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState<string | null>(null);
  // Phase 18 — optional proof image. When set, submit upgrades from
  // JSON to multipart/form-data so the file rides along.
  const [proofBlob, setProofBlob] = useState<Blob | null>(null);
  // Phase 17 — sub-agent target: 'wallet' (existing) or 'deposit' (new).
  // Player subjects ignore this — they only have a wallet.
  const [subagentTarget, setSubagentTarget] = useState<SubagentTarget>('wallet');

  // Fetch own pool on mount so the "Your pool" chip is accurate at
  // the exact moment the agent is about to submit (the chrome-chip
  // cache could be stale by a few seconds).
  useEffect(() => {
    if (myAgentId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get<{ agent: { wallet_balance: number } }>(`/admin/agents/${myAgentId}`);
        if (!cancelled) setMyPool(Number(r.agent.wallet_balance ?? 0));
      } catch { /* leave myPool null → "—" displayed */ }
    })();
    return () => { cancelled = true; };
  }, [myAgentId]);

  const amountInt = (() => {
    const n = Number(amount);
    return Number.isFinite(n) && n > 0 && Math.floor(n) === n ? n : 0;
  })();
  // Reason is now OPTIONAL (2026-08-06 UX fix — one fewer thing to type
  // for the common case). When blank we send a sensible default so the
  // audit-log row is never empty.
  const overdraw = myPool != null && amountInt > myPool;
  const canSubmit = !busy && amountInt > 0 && !overdraw;
  const noteToSend = reason.trim() || 'Direct recharge';

  /**
   * Phase 18 — send helper. Upgrades to multipart/form-data when a
   * proof blob is attached so the file rides along with the JSON
   * fields. Server-side multer parses both shapes.
   */
  async function sendAdjust(path: string, payload: Record<string, unknown>) {
    if (proofBlob) {
      const fd = new FormData();
      for (const [k, v] of Object.entries(payload)) {
        if (v === undefined || v === null) continue;
        fd.append(k, String(v));
      }
      fd.append('proof', proofBlob, 'screenshot.jpg');
      await adminApi.postForm(path, fd);
    } else {
      await adminApi.post(path, payload);
    }
  }

  async function submit() {
    if (!canSubmit) return;
    setBusy(true); setErr(null);
    try {
      if (subject.kind === 'player') {
        if (myAgentId == null) throw new Error(t('agent.directRecharge.modal.notSignedIn'));
        await sendAdjust(
          `/admin/agents/${myAgentId}/players/${subject.id}/recharge`,
          { amount: amountInt, note: noteToSend }
        );
      } else {
        // Phase 17 — route by sub-agent target.
        const path = subagentTarget === 'wallet'
          ? `/admin/me-agent/sub-agents/${subject.id}/wallet/topup`
          : `/admin/me-agent/sub-agents/${subject.id}/deposit/topup`;
        await sendAdjust(path, { amount: amountInt, note: noteToSend });
      }
      // Refresh the chrome wallet chip + any other listener of the
      // shared agent-wallet store so it shows the new pool balance.
      await refreshAgentWallet();
      onDone();
    } catch (e) {
      setErr((e as ApiError).message || t('agent.directRecharge.modal.failed'));
    } finally {
      setBusy(false);
    }
  }

  const inputCls = `w-full rounded-xl px-3 py-2 text-[14px]
                    bg-white dark:bg-arena-800/60
                    border border-arena-200/55 dark:border-arena-600/40
                    text-neutral-900 dark:text-white
                    placeholder-arena-400 dark:placeholder-neon-200/40
                    outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400/40`;

  const modal = (
    <div className="fixed inset-0 z-[8100] grid place-items-end sm:place-items-center
                    bg-black/60 backdrop-blur-sm p-0 sm:p-4 overflow-y-auto"
         onClick={busy ? undefined : onClose}
         role="dialog" aria-modal="true">
      <motion.div
        initial={{ y: 24, opacity: 0, scale: 0.97 }}
        animate={{ y: 0,  opacity: 1, scale: 1 }}
        exit={{ y: 16, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md mx-auto rounded-t-2xl sm:rounded-3xl
                   bg-white dark:bg-arena-900
                   border border-arena-200/60 dark:border-arena-600/50
                   shadow-[0_20px_60px_rgba(0,0,0,0.45)] overflow-hidden"
      >
        {/* Title strip */}
        <div className="px-5 py-3 border-b border-arena-200/40 dark:border-arena-700/40
                        bg-arena-50/40 dark:bg-arena-900/40
                        flex items-center gap-2">
          <span className="grid place-items-center w-8 h-8 rounded-xl shrink-0
                           bg-emerald-50 text-emerald-700 border border-emerald-200/60
                           dark:bg-emerald-400/15 dark:text-emerald-300 dark:border-emerald-400/30">
            <Icon name="arrow_upward" size={18} filled />
          </span>
          <div className="text-[15px] font-bold text-neutral-900 dark:text-white truncate flex-1">
            {subject.kind === 'player'
              ? t('agent.directRecharge.modal.title.player')
              : t('agent.directRecharge.modal.title.subagent')}
          </div>
          <button onClick={onClose} disabled={busy}
            className="grid place-items-center w-8 h-8 rounded-full text-arena-500 dark:text-neon-200/65
                       hover:bg-arena-100/60 dark:hover:bg-arena-700/60 disabled:opacity-40"
            aria-label={t('agent.directRecharge.modal.close')}>
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* Identity card — same shape as the admin modal's, with an
            explicit PLAYER / SUB-AGENT badge so the agent can't
            misread the entity kind. The kind discriminator is a
            critical safety check given that some sub-agents and
            players share usernames in practice. */}
        <div className="px-5 py-4 border-b border-arena-200/40 dark:border-arena-700/40 space-y-3">
          <SubjectIdentityCard subject={subject} />
          {/* My-pool chip is the funding-source counterpart to the
              parent chip in the admin modal — except here the source
              is fixed (always the calling agent's own pool), so we
              just surface the figure for confidence. Red when
              negative so a stuck negative pool is impossible to miss. */}
          <YourPoolChip pool={myPool} username={myUsername} />
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          {/* Phase 17 — target pool toggle (sub-agent only). Wallet =
              day-to-day pool; Deposit = collateral. Both fund from your
              own operating wallet. Player subjects skip this entirely
              since players only have one wallet. */}
          {subject.kind === 'subagent' && (
            <div>
              <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1.5 font-semibold">
                {t('agent.directRecharge.modal.targetPool')}
              </span>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => setSubagentTarget('wallet')}
                  className={`px-3 py-2 rounded-xl text-left transition active:scale-[0.98] text-[12.5px] font-bold
                    ${subagentTarget === 'wallet'
                      ? 'bg-cyan-600 text-white border border-cyan-600 shadow-[0_2px_10px_rgba(14,165,233,0.35)]'
                      : 'bg-white dark:bg-arena-800/60 text-arena-700 dark:text-neon-200/85 border border-arena-200/55 dark:border-arena-600/40 hover:bg-arena-50 dark:hover:bg-arena-800'}`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="account_balance_wallet" size={14} filled={subagentTarget === 'wallet'} />
                    {t('agent.directRecharge.modal.operatingWallet')}
                  </span>
                  <span className="block text-[10px] font-normal opacity-80 leading-tight">
                    {t('agent.directRecharge.modal.dayToDay')}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setSubagentTarget('deposit')}
                  className={`px-3 py-2 rounded-xl text-left transition active:scale-[0.98] text-[12.5px] font-bold
                    ${subagentTarget === 'deposit'
                      ? 'bg-emerald-600 text-white border border-emerald-600 shadow-[0_2px_10px_rgba(16,185,129,0.35)]'
                      : 'bg-white dark:bg-arena-800/60 text-arena-700 dark:text-neon-200/85 border border-arena-200/55 dark:border-arena-600/40 hover:bg-arena-50 dark:hover:bg-arena-800'}`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="savings" size={14} filled={subagentTarget === 'deposit'} />
                    {t('agent.directRecharge.modal.securityDeposit')}
                  </span>
                  <span className="block text-[10px] font-normal opacity-80 leading-tight">
                    {t('agent.directRecharge.modal.collateral')}
                  </span>
                </button>
              </div>
            </div>
          )}

          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">
              {t('agent.directRecharge.modal.amount')}
            </span>
            <input
              type="number" inputMode="numeric" min={1} step={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="1000"
              className={inputCls + ' font-mono tabular-nums text-[16px]'}
              autoFocus
            />
            {/* Quick-amount chips — one tap fills the common values so the
                broker rarely types a number (2026-08-06 UX fix). */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {[500, 1000, 2000, 5000].map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setAmount(String(q))}
                  className={`px-3 py-1.5 rounded-full text-[12.5px] font-bold tabular-nums transition active:scale-95 border
                    ${amountInt === q
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-[0_1px_6px_rgba(16,185,129,0.35)]'
                      : 'bg-white dark:bg-arena-800/60 text-arena-700 dark:text-neon-200/85 border-arena-200/55 dark:border-arena-600/40 hover:bg-arena-50 dark:hover:bg-arena-800'}`}
                >
                  ₹{q.toLocaleString('en-IN')}
                </button>
              ))}
            </div>
            {overdraw && (
              <span className="block text-[11.5px] text-rose-700 dark:text-rose-300 mt-1 leading-tight">
                {t('agent.directRecharge.modal.overdraw', { pool: (myPool ?? 0).toLocaleString('en-IN') })}
              </span>
            )}
          </label>

          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">
              {t('agent.directRecharge.modal.reason')}
              <span className="ml-1 normal-case tracking-normal text-arena-400 dark:text-neon-200/45 font-normal">· optional</span>
            </span>
            <input
              type="text" value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('agent.directRecharge.modal.reasonPlaceholder')}
              maxLength={190}
              className={inputCls}
            />
            <span className="block text-[10.5px] text-arena-500 dark:text-neon-200/55 mt-1 leading-tight">
              {t('agent.directRecharge.modal.reasonHint')}
            </span>
          </label>

          {/* Phase 18 — optional payment screenshot. Stored against
              the synthetic queue row the server creates; visible to
              the recipient via the History tab. */}
          <ProofImagePicker
            onFile={(b) => setProofBlob(b)}
            onClear={() => setProofBlob(null)}
            onError={(m) => setErr(m)}
            label={t('agent.directRecharge.modal.proofLabel')}
            hint={t('agent.directRecharge.modal.proofHint')}
          />

          {/* Move preview */}
          <div className="rounded-xl bg-arena-50/70 dark:bg-arena-800/40
                          border border-arena-200/55 dark:border-arena-600/40
                          px-3 py-2 text-[12.5px] leading-snug">
            {amountInt <= 0 ? (
              <span className="text-arena-500 dark:text-neon-200/55 italic">
                {t('agent.directRecharge.modal.previewEmpty')}
              </span>
            ) : (
              <span>
                <b className="text-emerald-700 dark:text-emerald-300">↑ ₹{amountInt.toLocaleString('en-IN')}</b>
                {/* 2026-05-18 — Use display name for sub-agents (peer view);
                    keep @username for players. The connective sentence is
                    fully translated; amount + name/pool interpolated. */}
                {' '}{t('agent.directRecharge.modal.previewBody', {
                  name: subject.kind === 'player'
                    ? `@${subject.username}`
                    : (((subject as { displayName?: string }).displayName || '').trim() || '—'),
                  pool: subject.kind === 'player'
                    ? t('agent.directRecharge.modal.pool.playerWallet')
                    : (subagentTarget === 'wallet'
                        ? t('agent.directRecharge.modal.pool.operatingWallet')
                        : t('agent.directRecharge.modal.pool.securityDeposit')),
                })}
              </span>
            )}
          </div>

          {err && (
            <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200/60 dark:border-rose-400/30 text-rose-700 dark:text-rose-200 text-sm px-3 py-2">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-arena-200/40 dark:border-arena-700/40
                        bg-arena-50/30 dark:bg-arena-900/40
                        flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 rounded-full text-[13px] font-semibold
                       bg-white dark:bg-arena-700/40
                       border border-arena-200/50 dark:border-arena-600/40
                       text-arena-700 dark:text-neon-200/75
                       hover:bg-arena-50 dark:hover:bg-arena-700/60">
            {t('agent.directRecharge.modal.cancel')}
          </button>
          <button onClick={submit} disabled={!canSubmit}
            className="px-4 py-2 rounded-full text-[13px] font-bold inline-flex items-center gap-1.5 text-white
                       bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700
                       shadow-[0_2px_10px_rgba(16,185,129,0.35)] disabled:opacity-50 disabled:cursor-not-allowed">
            {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="check" size={14} />}
            {t('agent.directRecharge.modal.send', { amount: amountInt || 0 })}
          </button>
        </div>
      </motion.div>
    </div>
  );

  return createPortal(modal, document.body);
}

// ─── Subject identity card ────────────────────────────────────────

function SubjectIdentityCard({ subject }: { subject: AgentDirectAdjustSubject }) {
  const t = useT();
  if (subject.kind === 'player') {
    const displayName = subject.displayName || subject.username;
    return (
      <div className="flex items-start gap-3">
        <AdminAvatar url={subject.avatarUrl ?? null} username={subject.username} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[16px] sm:text-[17px] font-display font-extrabold text-neutral-900 dark:text-white truncate leading-tight">
              {displayName}
            </span>
            <EntityBadge kind="player" />
          </div>
          <div className="text-[12px] text-arena-600 dark:text-neon-200/70 truncate mt-0.5">
            @{subject.username} <span className="text-arena-400">·</span> #{subject.id}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[15px] font-display font-extrabold text-sky-700 dark:text-sky-300 tabular-nums leading-none">
            ₹{subject.walletBalance.toLocaleString('en-IN')}
          </div>
          <div className="text-[10px] text-arena-500 dark:text-neon-200/55 mt-0.5 leading-none">{t('agent.directRecharge.label.wallet')}</div>
        </div>
      </div>
    );
  }
  // sub-agent — 2026-05-18 identity masking: peer view uses
  // displayName (composed first/last from the backend). Username
  // is NOT rendered for sub-agents. Falls back to a dash when the
  // displayName isn't populated yet.
  const subagentDisplayName = (subject.displayName || '').trim() || '—';
  return (
    <div className="flex items-start gap-3">
      <AdminAvatar url={subject.avatarUrl ?? null} username={subagentDisplayName} size="md" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[16px] sm:text-[17px] font-display font-extrabold text-neutral-900 dark:text-white truncate leading-tight">
            <span className={(subject.displayName || '').trim() ? '' : 'opacity-60'}>
              {subagentDisplayName}
            </span>
          </span>
          <EntityBadge kind="subagent" />
          {/* Level chip removed — agents must not see sub-agent levels (policy 2026-05-25). */}
        </div>
        <div className="text-[12px] text-arena-600 dark:text-neon-200/70 truncate mt-0.5">
          {t('agent.directRecharge.modal.yourDirectSubagent')}
          <span className="text-arena-400"> · #{subject.id}</span>
        </div>
        {subject.uaid && (
          <div className="text-[11px] text-arena-600 dark:text-neon-200/65 mt-1 inline-flex items-center gap-1 font-mono">
            <Icon name="qr_code_2" size={11} className="text-arena-500 dark:text-neon-200/65" />
            {subject.uaid}
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className={`text-[15px] font-display font-extrabold tabular-nums leading-none
          ${subject.poolBalance >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600 dark:text-red-400'}`}>
          ₹{subject.poolBalance.toLocaleString('en-IN')}
        </div>
        <div className="text-[10px] text-arena-500 dark:text-neon-200/55 mt-0.5 leading-none">{t('agent.directRecharge.label.pool')}</div>
      </div>
    </div>
  );
}

function EntityBadge({ kind }: { kind: 'player' | 'subagent' }) {
  const t = useT();
  const cls = kind === 'player'
    ? 'bg-sky-600 text-white dark:bg-sky-500 shadow-[0_1px_3px_rgba(14,165,233,0.4)]'
    : 'bg-emerald-600 text-white dark:bg-emerald-500 shadow-[0_1px_3px_rgba(16,185,129,0.4)]';
  return (
    <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.12em] whitespace-nowrap ${cls}`}>
      {kind === 'player' ? t('agent.directRecharge.badge.player') : t('agent.directRecharge.badge.subagent')}
    </span>
  );
}

function YourPoolChip({ pool, username }: { pool: number | null; username: string }) {
  const t = useT();
  const poolNeg = pool != null && pool < 0;
  return (
    <div className="rounded-xl bg-amber-50/60 dark:bg-amber-500/10
                    border border-amber-200/50 dark:border-amber-400/20
                    px-3 py-2 flex items-center gap-2">
      <span className="grid place-items-center w-7 h-7 rounded-full shrink-0
                       bg-amber-100 text-amber-800
                       dark:bg-amber-400/20 dark:text-amber-200">
        <Icon name="account_balance_wallet" size={14} filled />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[9.5px] font-extrabold uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300 leading-none">
          {t('agent.directRecharge.modal.fundingSource')}
        </div>
        <div className="text-[13px] font-semibold text-neutral-900 dark:text-white truncate leading-tight mt-1">
          @{username} <span className="text-arena-500 dark:text-neon-200/55 font-normal">{t('agent.directRecharge.modal.you')}</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-[13px] font-display font-extrabold tabular-nums leading-none
          ${poolNeg ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-300'}`}>
          {pool == null ? '—' : `₹${pool.toLocaleString('en-IN')}`}
        </div>
        <div className="text-[9.5px] text-amber-700/80 dark:text-amber-200/70 leading-none mt-0.5">
          {t('agent.directRecharge.modal.yourPool')}
        </div>
      </div>
    </div>
  );
}
